<script setup lang="ts">
const props = withDefaults(defineProps<{
  src?: string | null
  alt?: string
  loading?: 'lazy' | 'eager'
  decoding?: 'async' | 'sync' | 'auto'
  fetchpriority?: 'high' | 'low' | 'auto'
  progressive?: boolean
}>(), {
  src: '',
  alt: '',
  loading: 'lazy',
  decoding: 'async',
  fetchpriority: 'auto',
  progressive: true
})

const emit = defineEmits<{
  (event: 'load', payload?: Event): void
  (event: 'error', payload?: Event): void
}>()

const imgRef = ref<HTMLImageElement | null>(null)
const isLoaded = ref(false)
const isError = ref(false)

const resetState = () => {
  isLoaded.value = false
  isError.value = false
}

const markLoaded = (event?: Event) => {
  if (isLoaded.value) {
    return
  }
  isLoaded.value = true
  emit('load', event)
}

const syncLoadedState = () => {
  const img = imgRef.value
  if (!img) {
    return
  }
  if (img.complete && img.naturalWidth > 0) {
    markLoaded()
  } else if (img.complete && img.currentSrc) {
    onError()
  }
}

const onLoad = (event: Event) => {
  markLoaded(event)
}

const onError = (event?: Event) => {
  isError.value = true
  isLoaded.value = true
  emit('error', event)
}

watch(
  () => props.src,
  () => {
    resetState()
    nextTick(() => {
      syncLoadedState()
    })
  }
)

onMounted(() => {
  syncLoadedState()
})
</script>

<template>
  <img
    ref="imgRef"
    :src="src || ''"
    :alt="alt"
    :loading="loading"
    :decoding="decoding"
    :fetchpriority="fetchpriority"
    :class="{
      'blog-progressive-img': progressive,
      'is-loaded': isLoaded,
      'is-error': isError
    }"
    @load="onLoad"
    @error="onError"
  />
</template>
